import { useId } from "react";
import { C } from "./constants";

export function Btn({ children, onClick, variant = "primary", disabled, style }) {
  const variants = {
    primary: { background: C.teal, color: C.white },
    secondary: { background: C.tealLight, color: C.tealDark, border: `1px solid ${C.border}` },
    ghost: { background: "transparent", color: C.teal },
    gold: { background: C.gold, color: "#333" },
    danger: { background: C.red, color: C.white },
  };
  const v = variants[variant] || variants.primary;
  const base = {
    padding: "9px 20px",
    borderRadius: 8,
    border: "none",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    transition: "opacity 0.15s",
  };
  const merged = Object.assign({}, base, v, style || {});
  return (
    <button onClick={onClick} disabled={disabled} style={merged}>
      {children}
    </button>
  );
}

// A slide switch: right = ON (green), left = OFF (grey). role="switch" + aria-checked
// keep it accessible + testable (getByRole("switch", { name })). size="lg" makes it
// roughly the height of a button (for use in place of an on/off button).
export function SlideToggle({ checked, onChange, label, disabled, size }) {
  const big = size === "lg";
  const w = big ? 62 : 48;
  const h = big ? 32 : 26;
  const knob = big ? 26 : 20;
  const pad = 3;
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none mb-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => {
          if (!disabled) onChange(!checked);
        }}
        style={{
          width: w,
          height: h,
          borderRadius: h / 2,
          border: "none",
          background: checked ? "#2E7D32" : "#B0BEC5",
          position: "relative",
          cursor: disabled ? "default" : "pointer",
          flexShrink: 0,
          transition: "background 0.15s",
          padding: 0,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: pad,
            left: checked ? w - knob - pad : pad,
            width: knob,
            height: knob,
            borderRadius: "50%",
            background: "white",
            transition: "left 0.15s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
          }}
        />
      </button>
      <span className="text-[14px] text-zephyr-text font-semibold">{label}</span>
    </label>
  );
}

export function Card({ children, style }) {
  const s = Object.assign(
    {
      background: C.white,
      borderRadius: 12,
      border: `1px solid ${C.border}`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      padding: 24,
    },
    style || {}
  );
  return <div style={s}>{children}</div>;
}

export function TxtInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  error,
  type = "text",
  readOnly,
  rows,
}) {
  const inputId = useId();
  const inputStyle = {
    width: "100%",
    padding: "9px 13px",
    borderRadius: 7,
    border: `1px solid ${error ? C.red : C.border}`,
    fontSize: 14,
    color: C.text,
    fontFamily: "inherit",
    boxSizing: "border-box",
    background: readOnly ? C.offWhite : C.white,
  };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label
          htmlFor={inputId}
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: C.text,
            marginBottom: 5,
          }}
        >
          {label}
          {required && <span style={{ color: C.red }}> *</span>}
        </label>
      )}
      {rows ? (
        <textarea
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          style={Object.assign({}, inputStyle, { resize: "vertical" })}
        />
      ) : (
        <input
          id={inputId}
          type={type}
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={inputStyle}
        />
      )}
      {error && <div style={{ color: C.red, fontSize: 12, marginTop: 3 }}>{error}</div>}
    </div>
  );
}
